import PropTypes from 'prop-types'
import { useCallback, useMemo, useState } from 'react'

import { Button, Layout } from '@loadsmart/loadsmart-ui'
import { Tag } from '@loadsmart/miranda-react'

import { Equipment } from 'legacy/components/LoadOffer/Equipment'
import { Mileage } from 'legacy/components/LoadOffer/Mileage'
import { OfferTypeBadges } from 'legacy/components/LoadOffer/OfferTypeBadges'
import { Price } from 'legacy/components/LoadOffer/Price'
import { StrikeThroughPrice } from 'legacy/components/LoadOffer/StrikeThroughPrice'
import { formatNumber } from 'legacy/utils/formatNumber'
import { getLoadOfferPriceBreakdown } from 'legacy/utils/getLoadOfferPriceBreakdown'
import { getLoadOfferType, OFFER_TYPES } from 'legacy/utils/getLoadOfferType'

import { hasBidPriceChanged } from 'loads/domain/Bid'
import { BidButton } from 'loads/ui/offer-card/buttons/BidButton'
import { OfferChatButton } from 'offers/ui/OfferChatButton'
import { RateBreakdown } from 'offers/ui/RateBreakdown'

import { isPalletized } from '../utils/loadUtils'
import { ExpirationInfo } from './ExpirationInfo'
import { LoadRequirements } from './LoadRequirements'
import { LoadStop } from './LoadStop'
import { OfferCardFeedback } from './OfferCardFeedback'
import {
  BottomSectionGroupMobile,
  BottomSectionMobile,
  CardColumnsContainerMobile,
  CardMobile,
  FeatureTags,
  FooterDividerMobile,
  LeftColumnMobile,
  LoadRequirementsContainerMobile,
  RightColumnMobile,
  RouteMobile,
  TopSectionMobile,
} from './OfferCardMobile.styles'
import { OfferedBy } from './OfferedBy'

function formatOfferCardMobileMileage({ mileage }) {
  return `${formatNumber(mileage)} mi`
}

function OfferCardMobile(props) {
  const {
    analyticsContext,
    activeBid,
    awardedBid,
    context,
    countdown,
    showBidCountdown,
    displayChat,
    forceMobile,
    handleBid,
    handleBook,
    handleRejectLoad,
    hasActiveDNU,
    isBidDisabled,
    isCarrierReady,
    isPublicRelatedLoad,
    loadOffer,
    offeredBy,
    primaryActionButtonProps,
    requirements,
    showFeedback,
    showOnlyTwoStops,
    showPTL,
    displayTooltip,
  } = props

  const [isExpired, setExpired] = useState(false)
  const { price, daimler_vip, created_at, expires_at, biddable } = loadOffer
  const { stops, temperature, mileage, deadhead, ref_number, mode } = loadOffer.load

  const equipment_type = loadOffer.load.equipment_type

  const offerType = getLoadOfferType(loadOffer)
  const isContracted = offerType === OFFER_TYPES.CONTRACTED
  const isLoadCandidate = offerType === OFFER_TYPES.LOAD_CANDIDATE
  const showStrikeThroughPrice = hasBidPriceChanged(awardedBid)
  const { haul, fuel } = getLoadOfferPriceBreakdown(loadOffer)
  const showPriceBreakdown = Boolean(haul && fuel)

  const renderedStops = useMemo(() => {
    if (stops.length <= 0) {
      return null
    }

    const elements = []
    const isMultiStop = stops.length > 2

    for (let i = 0; i < stops.length; i++) {
      const stop = stops[i]
      const isFirst = i === 0
      const isLast = i === stops.length - 1

      if (!showOnlyTwoStops || isFirst || isLast) {
        elements.push(
          <LoadStop
            isPickup={isFirst}
            isDelivery={isLast}
            stop={stop}
            isVip={daimler_vip}
            isMultiStop={isMultiStop}
            offerType={offerType}
            key={i}
            forceMobile={forceMobile}
            deadhead={deadhead}
          />
        )
      }
    }
    return elements
  }, [stops, deadhead, daimler_vip, offerType, forceMobile, showOnlyTwoStops])

  const handleCardClick = useCallback(
    event => {
      if (isExpired) {
        event.preventDefault()
        event.stopPropagation()
      }
    },
    [isExpired]
  )

  const hasRequirements = requirements.length > 0
  const shouldDisplayChat = displayChat && !awardedBid && !isPublicRelatedLoad

  return (
    <CardMobile isVip={daimler_vip} onClick={handleCardClick}>
      <CardColumnsContainerMobile>
        <LeftColumnMobile>
          <RouteMobile>{renderedStops}</RouteMobile>
        </LeftColumnMobile>
        <RightColumnMobile>
          {!loadOffer.load.all_appointments_confirmed ? (
            <Tag variant="danger">APPOINTMENTS PENDING</Tag>
          ) : (
            <div></div>
          )}
          <TopSectionMobile>
            <OfferTypeBadges
              isVip={daimler_vip}
              isContracted={isContracted}
              isLoadCandidate={isLoadCandidate}
            />

            <Equipment
              mode={showPTL ? mode : null}
              equipmentType={equipment_type}
              temperature={temperature}
              forceMobile={forceMobile}
            />
          </TopSectionMobile>
          {isPalletized(loadOffer?.load) && <FeatureTags>Palletized</FeatureTags>}
        </RightColumnMobile>
      </CardColumnsContainerMobile>

      {hasRequirements && (
        <LoadRequirementsContainerMobile>
          <LoadRequirements requirements={requirements} />
        </LoadRequirementsContainerMobile>
      )}

      {(isContracted || isLoadCandidate) && (
        <ExpirationInfo
          isContracted={isContracted}
          isLoadCandidate={isLoadCandidate}
          created_at={new Date(created_at)}
          expires_at={new Date(expires_at)}
          onExpiration={setExpired}
          forceMobile={forceMobile}
        />
      )}

      <Layout.Stack space="none">
        <FooterDividerMobile />
        <BottomSectionMobile space="s" align="center">
          <BottomSectionGroupMobile align="center" space="s">
            <Layout.Stack space="none">
              {showStrikeThroughPrice && <StrikeThroughPrice price={awardedBid.bookPrice} />}
              <Price price={awardedBid?.price ?? price} />
            </Layout.Stack>
            <Mileage
              mileage={mileage}
              forceMobile
              size="default"
              formatter={formatOfferCardMobileMileage}
            />
          </BottomSectionGroupMobile>
          <BottomSectionGroupMobile align="center" space="s" justify="flex-end">
            {shouldDisplayChat && (
              <OfferChatButton
                refNumber={ref_number}
                isLoadCandidate={isLoadCandidate}
                isContracted={isContracted}
                stops={stops}
                context={context}
                analyticsContext={analyticsContext}
              />
            )}
            {!isCarrierReady || isPublicRelatedLoad ? (
              <Button disabled data-testid="OfferCardMobile__NotEligibleBookButton">
                {primaryActionButtonProps.text}
              </Button>
            ) : !isExpired ? (
              <>
                {isContracted && (
                  <Button onClick={handleRejectLoad} variant="warning">
                    Reject
                  </Button>
                )}
                {biddable && (
                  <BidButton
                    displayCountdown={showBidCountdown}
                    displayTooltip={displayTooltip}
                    isMobile={true}
                    onClick={handleBid}
                    price={Boolean(activeBid) && activeBid.price}
                    disabled={isBidDisabled}
                    countdown={countdown}
                  />
                )}
                <Button
                  variant="primary"
                  title="Book"
                  type="button"
                  daimler_vip={daimler_vip}
                  onClick={handleBook}
                  disabled={hasActiveDNU}
                  {...primaryActionButtonProps}
                />
              </>
            ) : (
              <Button disabled>Expired</Button>
            )}
          </BottomSectionGroupMobile>
        </BottomSectionMobile>
        {showPriceBreakdown && (
          <Layout.Box style={{ paddingTop: 0 }}>
            <RateBreakdown haulPrice={haul} fuelPrice={fuel} />
          </Layout.Box>
        )}
      </Layout.Stack>
      <OfferedBy name={offeredBy} />
      {showFeedback && <OfferCardFeedback uuid={loadOffer.uuid} forceMobile={forceMobile} />}
    </CardMobile>
  )
}

OfferCardMobile.propTypes = {
  activeBid: PropTypes.shape(),
  analyticsContext: PropTypes.string.isRequired,
  context: PropTypes.string.isRequired,
  countdown: PropTypes.string.isRequired,
  showBidCountdown: PropTypes.bool.isRequired,
  forceMobile: PropTypes.bool.isRequired,
  handleBid: PropTypes.func.isRequired,
  handleBook: PropTypes.func.isRequired,
  hasActiveDNU: PropTypes.bool.isRequired,
  isBidDisabled: PropTypes.bool.isRequired,
  isCarrierReady: PropTypes.bool,
  isPublicRelatedLoad: PropTypes.bool,
  loadOffer: PropTypes.shape().isRequired,
  requirements: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      params: PropTypes.shape({}),
    })
  ).isRequired,
  showOnlyTwoStops: PropTypes.bool.isRequired,
  showFeedback: PropTypes.bool.isRequired,
  displayChat: PropTypes.bool,
  offeredBy: PropTypes.string,
  awardedBid: PropTypes.shape({
    bookPrice: PropTypes.string,
    price: PropTypes.string,
  }),
  primaryActionButtonProps: PropTypes.shape({
    children: PropTypes.string,
  }),
  showPTL: PropTypes.bool,
}

OfferCardMobile.defaultProps = {
  activeBid: undefined,
  isCarrierReady: false,
  isPublicRelatedLoad: false,
  displayChat: true,
  showPTL: false,
}

export { OfferCardMobile }
